import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { PrimeGlyph } from "./PrimeGlyph";
import type { PrimeGlyphName } from "./prime-glyphs";

// THE PILOT'S `UiService.showToast(severity, title, msg, life, sticky, key)` AND
// `clearToasts(key)`. LIKE PRIMENG'S `p-toast` IT STACKS: EVERY MESSAGE IS ITS OWN PANEL,
// NEWEST AT THE BOTTOM, 25REM WIDE, TOP-CENTER AT 100PX (`_utils.scss`). A MESSAGE CLOSES
// AFTER `life` MS (ANGULAR'S `showToast` DEFAULT, 3000) UNLESS IT IS STICKY, AND A `key`
// GROUPS MESSAGES SO THEY CAN BE CLEARED TOGETHER. `onClose` FIRES ONLY WHEN THE USER
// CLOSES ONE, NEVER FOR A CLEAR OR A TIMEOUT - PRIMENG'S `clear()` RAISES NO `onClose`
// EITHER, WHICH IS WHAT MAKES THE PROFILE'S DISMISSAL LEDGER TRUSTWORTHY (ISSUE #113).
export type ToastSeverity = "success" | "info" | "warning" | "error";

export interface ToastOptions {
  life?: number;
  sticky?: boolean;
  key?: string;
  onClose?: () => void;
}

type ShowToast = (severity: ToastSeverity, title: string, detail: string, options?: ToastOptions) => void;

interface ToastApi {
  show: ShowToast;
  clear: (key?: string) => void;
}

const ToastContext = createContext<ToastApi>({ show: () => {}, clear: () => {} });

// PRIMENG'S `.p-toast-message-*` COLORS (THE SAME IN BOTH PILLARS THEMES): A TINTED PANEL
// WITH A 1PX BORDER, TEXT AND ICON IN THE SEVERITY'S DARK SHADE, AND ITS OWN ICON
const TOAST_LOOK: Record<ToastSeverity, { bg: string; border: string; text: string; icon: PrimeGlyphName }> = {
  success: { bg: "#d4edda", border: "#c3e6cb", text: "#155724", icon: "check" },
  error: { bg: "#f8d7da", border: "#f5c6cb", text: "#721c24", icon: "times-circle" },
  warning: { bg: "#fff3cd", border: "#ffeeba", text: "#856404", icon: "exclamation-triangle" },
  info: { bg: "#cce5ff", border: "#b8daff", text: "#004085", icon: "info-circle" },
};

interface Message {
  id: number;
  severity: ToastSeverity;
  title: string;
  detail: string;
  key: string | undefined;
  onClose: (() => void) | undefined;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const nextId = useRef(1);
  // A TIMED MESSAGE'S TIMER, SO A CLEAR CAN CANCEL IT
  const timers = useRef(new Map<number, number>());

  const remove = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t !== undefined) window.clearTimeout(t);
    timers.current.delete(id);
    setMessages((list) => list.filter((m) => m.id !== id));
  }, []);

  const show = useCallback<ShowToast>(
    (severity, title, detail, options = {}) => {
      const id = nextId.current++;
      setMessages((list) => [...list, { id, severity, title, detail, key: options.key, onClose: options.onClose }]);
      // A HUMAN-VISIBLE LIFETIME, NOT SEQUENCING - THE ONE KIND OF TIMER THE HOUSE RULES ALLOW
      if (!options.sticky) timers.current.set(id, window.setTimeout(() => remove(id), options.life ?? 3000));
    },
    [remove],
  );

  const clear = useCallback((key?: string) => {
    setMessages((list) => {
      const gone = list.filter((m) => m.key === key);
      for (const m of gone) {
        const t = timers.current.get(m.id);
        if (t !== undefined) window.clearTimeout(t);
        timers.current.delete(m.id);
      }
      return list.filter((m) => m.key !== key);
    });
  }, []);

  const api = useMemo(() => ({ show, clear }), [show, clear]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        role="region"
        aria-label="Notifications"
        aria-live="polite"
        style={{
          position: "fixed",
          top: 100,
          left: "50%",
          transform: "translateX(-50%)",
          width: "25rem",
          maxWidth: "calc(100vw - 28px)",
          zIndex: 1400,
          pointerEvents: messages.length ? "auto" : "none",
        }}
      >
        {messages.map((m) => {
          const look = TOAST_LOOK[m.severity];
          return (
            <div
              key={m.id}
              role={m.severity === "error" ? "alert" : "status"}
              className="fadein animation-duration-500"
              style={{
                margin: "0 0 1rem 0",
                boxShadow: "0 0.25rem 0.75rem rgba(0, 0, 0, 0.1)",
                borderRadius: 4,
                background: look.bg,
                border: `1px solid ${look.border}`,
                color: look.text,
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", padding: "1rem" }}>
                <PrimeGlyph name={look.icon} size="2rem" />
                <div style={{ margin: "0 0 0 1rem", flex: "1 1 auto", minWidth: 0, overflowWrap: "anywhere" }}>
                  <div style={{ fontWeight: 700 }}>{m.title}</div>
                  <div style={{ margin: "0.5rem 0 0 0" }}>{m.detail}</div>
                </div>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => {
                    remove(m.id);
                    m.onClose?.();
                  }}
                  style={{
                    width: "2rem",
                    height: "2rem",
                    borderRadius: "50%",
                    border: 0,
                    background: "transparent",
                    color: look.text,
                    display: "grid",
                    placeItems: "center",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  <PrimeGlyph name="times" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

// THE SHOW FUNCTION, AS EVERY PAGE USES IT
export const useToast = () => useContext(ToastContext).show;
// SHOW AND CLEAR, FOR THE FEW PLACES THAT MANAGE A KEYED GROUP
export const useToasts = () => useContext(ToastContext);
